import type { Category } from "@/constants/categories";

type FoodProduct = {
  id: string;
  name: string;
  categorySlug: Category["slug"];
  brand: string;
  price: number;
  unit: string;
  image: string;
};

const IMG = {
  food: "https://images.unsplash.com/photo-1542838132-92c53300491e?crop=entropy&cs=srgb&fm=jpg&q=85",
  jar: "https://images.unsplash.com/photo-1589927986089-35812388d1f4?crop=entropy&cs=srgb&fm=jpg&q=85",
  tea: "https://images.unsplash.com/photo-1564890369478-c89ca6d9cde9?crop=entropy&cs=srgb&fm=jpg&q=85",
  sauce: "https://images.unsplash.com/photo-1472476443507-c7a5948772fc?crop=entropy&cs=srgb&fm=jpg&q=85",
  cereal: "https://images.unsplash.com/photo-1517456215183-9a2c3a1f7c68?crop=entropy&cs=srgb&fm=jpg&q=85",
  pasta: "https://images.unsplash.com/photo-1551462147-ff29053bfc14?crop=entropy&cs=srgb&fm=jpg&q=85",
};

const item = (
  id: string,
  name: string,
  brand: string,
  unit: string,
  price: number,
  image: string,
): FoodProduct => ({
  id,
  name,
  categorySlug: "food",
  brand,
  unit,
  price,
  image,
});

export const FOOD_PRODUCTS: FoodProduct[] = [
  // Pickles & Preserves (jars from page 1)
  item("food-mixed-pickle", "Mixed Pickle", "Mother's Recipe", "500g", 5.99, IMG.jar),
  item("food-mango-pickle", "Mango Pickle", "Mother's Recipe", "500g", 5.99, IMG.jar),
  item("food-lime-pickle", "Lime Pickle", "Mother's Recipe", "500g", 5.49, IMG.jar),
  item("food-garlic-pickle", "Garlic Pickle", "Mother's Recipe", "500g", 5.49, IMG.jar),
  item("food-chilli-pickle", "Chilli Pickle", "Mother's Recipe", "500g", 5.49, IMG.jar),
  item("food-lemon-pickle", "Lemon Pickle", "Mother's Recipe", "300g", 4.49, IMG.jar),
  item("food-tomato-chutney", "Tomato Chutney", "Kissan", "500g", 4.99, IMG.jar),
  item("food-tamarind-chutney", "Tamarind Chutney", "Maggi", "500g", 4.99, IMG.jar),
  item("food-red-chilli-paste", "Red Chilli Paste", "Nirav", "300g", 4.49, IMG.jar),
  item("food-ginger-garlic-paste", "Ginger Garlic Paste", "Eastern", "300g", 3.99, IMG.jar),
  item("food-coconut-chutney", "Coconut Chutney", "Eastern", "250g", 3.49, IMG.jar),
  item("food-green-chutney", "Green Chutney", "Shan", "300g", 3.99, IMG.jar),
  item("food-coriander-chutney", "Coriander Chutney", "Ahmed", "300g", 3.99, IMG.jar),
  item("food-mint-chutney", "Mint Chutney", "Ahmed", "300g", 3.99, IMG.jar),
  item("food-chickpeas-jar", "Chickpeas in Brine", "KTC", "400g", 2.49, IMG.jar),
  item("food-red-kidney-beans", "Red Kidney Beans", "KTC", "400g", 2.49, IMG.jar),
  item("food-baked-beans", "Baked Beans", "Heinz", "415g", 2.99, IMG.jar),

  // Honey & Syrups
  item("food-honey-pure", "Pure Honey", "Dabur", "500g", 8.99, IMG.jar),
  item("food-maple-syrup", "Virginia Green Garden Maple Syrup", "Virginia", "350ml", 9.99, IMG.jar),

  // Pasta & Noodles
  item("food-spaghetti-today", "Today Spaghetti", "Today", "500g", 3.49, IMG.pasta),
  item("food-pasta-fusilli", "Pasta Fusilli", "Barilla", "500g", 3.99, IMG.pasta),
  item("food-pasta-penne", "Pasta Penne", "Barilla", "500g", 3.99, IMG.pasta),

  // Tea & Coffee
  item("food-twinings-earl-grey", "Twinings Earl Grey Green Tea", "Twinings", "25 bags", 6.99, IMG.tea),
  item("food-twinings-english-breakfast", "Twinings English Breakfast Tea", "Twinings", "25 bags", 6.99, IMG.tea),
  item("food-twinings-green-jasmine", "Twinings Green Tea Jasmine", "Twinings", "25 bags", 6.99, IMG.tea),
  item("food-twinings-pure-green", "Twinings Pure Green Tea", "Twinings", "25 bags", 6.99, IMG.tea),
  item("food-twinings-ginger-green", "Twinings Ginger Green Tea", "Twinings", "25 bags", 6.99, IMG.tea),
  item("food-twinings-lemon-green", "Twinings Lemon Green Tea", "Twinings", "25 bags", 6.99, IMG.tea),
  item("food-twinings-mint-green", "Twinings Mint Green Tea", "Twinings", "25 bags", 6.99, IMG.tea),
  item("food-twix-coffee", "Twix Coffee", "Mars", "275g", 7.99, IMG.tea),

  // Sauces & Condiments (pages 2-3)
  item("food-pesto-calabrese", "Pesto alla Calabrese", "Barilla", "190g", 5.99, IMG.sauce),
  item("food-pesto-classic", "Pesto Classic", "Barilla", "190g", 5.99, IMG.sauce),
  item("food-pesto-rosso", "Pesto Rosso", "Barilla", "190g", 5.99, IMG.sauce),
  item("food-cholula-hot-sauce", "Cholula Original Hot Sauce", "Cholula", "150ml", 6.49, IMG.sauce),
  item("food-nandos-peri-peri", "Nando's Peri-Peri Sauce", "Nando's", "250ml", 5.99, IMG.sauce),
  item("food-heinz-ketchup", "Heinz Tomato Ketchup", "Heinz", "500ml", 4.99, IMG.sauce),
  item("food-sriracha", "Sriracha Hot Chilli Sauce", "Huy Fong", "435ml", 6.99, IMG.sauce),
  item("food-soy-sauce", "Soy Sauce", "Kikkoman", "250ml", 4.49, IMG.sauce),
  item("food-pasta-sauce-tomato", "Pasta Sauce Tomato & Basil", "Dolmio", "500g", 4.49, IMG.sauce),
  item("food-olive-oil-evoo", "Extra Virgin Olive Oil", "Filippo Berio", "500ml", 12.99, IMG.sauce),
  item("food-mayonnaise", "Mayonnaise", "Hellmann's", "400g", 5.49, IMG.sauce),

  // Spreads & Nut Butters (page 4)
  item("food-peanut-butter-crunchy", "Peanut Butter Crunchy", "Skippy", "500g", 6.99, IMG.jar),
  item("food-nutella", "Nutella Hazelnut Spread", "Ferrero", "750g", 9.99, IMG.jar),
  item("food-biscoff-spread", "Biscoff Spread", "Lotus", "400g", 7.49, IMG.jar),
  item("food-coconut-oil", "Coconut Oil", "KTC", "500ml", 6.99, IMG.jar),

  // Cereals & Breakfast (page 4)
  item("food-nesquik-cereal", "Nesquik Cereal", "Nestlé", "375g", 6.49, IMG.cereal),
  item("food-lion-cereal", "Lion Cereal", "Nestlé", "400g", 6.49, IMG.cereal),
  item("food-fitness-cereal", "Fitness Cereal", "Nestlé", "375g", 6.49, IMG.cereal),
  item("food-fitness-choco", "Fitness Chocolate Cereal", "Nestlé", "375g", 6.99, IMG.cereal),
  item("food-nesquik-drink", "Nesquik Chocolate Drink", "Nestlé", "500g", 7.99, IMG.cereal),
  item("food-custard-powder", "Custard Powder", "Bird's", "350g", 4.99, IMG.cereal),

  // Misc food items
  item("food-kit-kat-bar", "Kit Kat Chunky", "Nestlé", "40g", 1.99, IMG.food),
  item("food-desiccated-coconut", "Desiccated Coconut", "KTC", "250g", 2.99, IMG.food),
  item("food-vermicelli", "Vermicelli", "Bambino", "200g", 2.49, IMG.food),
];
